import { useMemo, useState } from "react";
import { Search, ShieldCheck, UserX, UserCheck } from "lucide-react";

export const UsersTab = ({ users, onUpdateStatus }) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch = !needle || [user.name, user.email, user.user_id]
        .some((value) => String(value || "").toLowerCase().includes(needle));
      const isActive = user.is_active !== false;
      const matchesStatus = status === "all"
        || (status === "active" && isActive)
        || (status === "suspended" && !isActive);
      return matchesSearch && matchesStatus;
    });
  }, [users, search, status]);

  const changeStatus = (user) => {
    const active = user.is_active !== false;
    if (active) {
      const reason = window.prompt("Why is this account being suspended?");
      if (!reason?.trim()) return;
      onUpdateStatus(user.user_id, false, reason.trim());
      return;
    }
    if (window.confirm(`Restore access for ${user.email}?`)) {
      onUpdateStatus(user.user_id, true, "");
    }
  };

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, or user ID"
            className="input-minimal pl-10"
          />
        </div>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="input-minimal md:w-48">
          <option value="all">All account statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <div className="px-4 py-2 rounded-lg bg-[#18181B] text-sm text-[#A1A1AA]">
          {filtered.length} shown
        </div>
      </div>

      <div className="glass-card overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-[#18181B]">
            <tr>
              <th className="text-left text-sm text-[#A1A1AA] font-medium p-3">User</th>
              <th className="text-left text-sm text-[#A1A1AA] font-medium p-3">Email</th>
              <th className="text-left text-sm text-[#A1A1AA] font-medium p-3">Roles</th>
              <th className="text-left text-sm text-[#A1A1AA] font-medium p-3">Status</th>
              <th className="text-left text-sm text-[#A1A1AA] font-medium p-3">Joined</th>
              <th className="text-right text-sm text-[#A1A1AA] font-medium p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => {
              const active = user.is_active !== false;
              return (
                <tr key={user.user_id} className="border-t border-[#27272A]">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {user.picture
                        ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                        : <div className="w-8 h-8 rounded-full bg-[#27272A] flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-[#71717A]" /></div>}
                      <div>
                        <p className="text-[#EDEDED]">{user.name || "Anonymous"}</p>
                        <p className="text-[11px] text-[#52525B]">{user.user_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[#A1A1AA] text-sm">{user.email}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles?.map((role) => (
                        <span key={role} className="px-2 py-0.5 text-xs rounded bg-[#27272A] text-[#A1A1AA]">{role}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${active ? "bg-[#22C55E]/10 text-[#22C55E]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>
                      {active ? "Active" : "Suspended"}
                    </span>
                    {!active && user.suspension_reason && (
                      <p className="text-[11px] text-[#71717A] mt-1 max-w-48">{user.suspension_reason}</p>
                    )}
                  </td>
                  <td className="p-3 text-[#71717A] text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => changeStatus(user)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${active ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#22C55E]/10 text-[#22C55E]"}`}
                    >
                      {active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      {active ? "Suspend" : "Restore"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
